import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import "./StudentTutorPage.css";
import "../../../styles/common.css";
import formatFeedback from "../../mainpages/coursepages/moduledetailpage/helpers/formatFeedback";

const StudentTutorPage = ({ setPageTitle }) => {
  const { tutorId, conversationId } = useParams();
  const [tutor, setTutor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [currentConversationId, setCurrentConversationId] =
    useState(conversationId);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (tutorId) {
      fetchTutorData();
    }
    if (conversationId) {
      fetchConversation();
    } else {
      setMessages([]);
      setIsLoading(false);
    }
  }, [tutorId, conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchTutorData = async () => {
    try {
      const getTutorsFunction = httpsCallable(functions, "getTutors");
      const result = await getTutorsFunction();

      if (result.data && result.data.tutors) {
        const foundTutor = result.data.tutors.find((t) => t.id === tutorId);
        if (foundTutor) {
          setTutor(foundTutor);
          setPageTitle(`AI Tutor: ${foundTutor.name}`);
        }
      }
    } catch (error) {
      console.error("Error fetching tutor:", error);
    }
  };

  const fetchConversation = async () => {
    setIsLoading(true);
    try {
      const getTutorConversation = httpsCallable(
        functions,
        "getTutorConversation"
      );
      const result = await getTutorConversation({ conversationId });

      if (result.data && result.data.conversation) {
        setMessages(result.data.conversation.messages || []);
        if (!tutor && result.data.conversation.tutorId) {
          const tutorId = result.data.conversation.tutorId;
          const getTutorsFunction = httpsCallable(functions, "getTutors");
          const tutorsResult = await getTutorsFunction();

          if (tutorsResult.data && tutorsResult.data.tutors) {
            const foundTutor = tutorsResult.data.tutors.find(
              (t) => t.id === tutorId
            );
            if (foundTutor) {
              setTutor(foundTutor);
              setPageTitle(`AI Tutor: ${foundTutor.name}`);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      // Add message to UI immediately for better UX
      const clientTimestamp = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        { content: newMessage, sender: "user", timestamp: clientTimestamp },
      ]);
      setNewMessage("");

      const processTutorInteraction = httpsCallable(
        functions,
        "processTutorInteraction"
      );
      const result = await processTutorInteraction({
        tutorId,
        message: newMessage,
        conversationId: currentConversationId,
      });

      if (result.data) {
        if (!currentConversationId && result.data.conversationId) {
          setCurrentConversationId(result.data.conversationId);
          window.history.replaceState(
            null,
            "",
            `/ai-tutors/${tutorId}/conversations/${result.data.conversationId}`
          );
        }

        // Handle context greeting for new conversations
        if (result.data.hasContextGreeting && result.data.allMessages) {
          // Replace all messages with the complete conversation including context greeting
          setMessages(
            result.data.allMessages.map((msg) => ({
              content: msg.content,
              sender: msg.sender,
              timestamp: msg.timestamp,
              isContextGreeting: msg.isContextGreeting || false,
            }))
          );
        } else {
          // Add AI response to messages
          setMessages((prev) => [
            ...prev,
            {
              content: result.data.response,
              sender: "assistant",
              timestamp: new Date().toISOString(),
            },
          ]);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          content:
            "Sorry, there was an error processing your message. Please try again.",
          sender: "system",
          isError: true,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Just now";
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format message content using the formatFeedback utility
  const formatMessageContent = (content) => {
    const formattedContent = formatFeedback.formatForDisplay(content);
    return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
  };

  if (isLoading) {
    return (
      <div className="stpage-loading-container">
        <div className="stpage-loading-ring"></div>
        <p>Loading conversation...</p>
      </div>
    );
  }

  if (!tutor) {
    return <div className="stpage-error-container">Tutor not found.</div>;
  }

  return (
    <div className="container stpage-container">
      <div className="stpage-tutor-info">
        <h1>{tutor.name}</h1>
        <p>{tutor.description}</p>
      </div>

      <div className="stpage-chat-container">
        <div className="stpage-messages-container">
          {messages.length === 0 ? (
            <div className="stpage-empty-chat">
              <p>Start a conversation with this AI tutor.</p>
              <p>
                The tutor will review your recent module feedback to provide
                personalized help.
              </p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={`stpage-message ${
                  message.sender === "user"
                    ? "stpage-user-message"
                    : message.sender === "assistant"
                    ? "stpage-assistant-message"
                    : "stpage-system-message"
                }`}
              >
                {message.isContextGreeting && (
                  <div className="stpage-context-indicator">
                    📝 Based on your recent module feedback
                  </div>
                )}
                <div className="stpage-message-content">
                  {message.sender === "assistant"
                    ? formatMessageContent(message.content)
                    : message.content}
                </div>
                <div className="stpage-message-time">
                  {formatTime(message.timestamp)}
                </div>

                {message.followUpQuestions &&
                  message.followUpQuestions.length > 0 && (
                    <div className="stpage-follow-up-questions">
                      {message.followUpQuestions.map((question, qIdx) => (
                        <button
                          key={qIdx}
                          className="stpage-follow-up-button"
                          onClick={() => setNewMessage(question)}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="stpage-message-form" onSubmit={handleSendMessage}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message here..."
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
          />
          <button
            type="submit"
            disabled={isSending || !newMessage.trim()}
            className={isSending ? "stpage-sending" : ""}
          >
            {isSending ? "Sending..." : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentTutorPage;
